import { cn } from '@quadralize/utils'
import { Basketball, ClockSquare, MoonStars, Sun } from '@solar-icons/react'
import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '../../data-display/avatar/avatar'
import type { HeaderProps } from '../types'

const CLOCK_FORMATTER = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
})
const CLOCK_UPDATE_INTERVAL_MS = 1000

export function Header({
  className,
  breadcrumbs,
  showClock = true,
  theme,
  onToggleTheme,
  onToggleSidebarMobile,
  user,
  userAction,
  themeButton,
  logo,
}: HeaderProps) {
  const LogoIcon = logo?.icon
  const [currentTime, setCurrentTime] = useState(() => CLOCK_FORMATTER.format(new Date()))

  useEffect(() => {
    if (!showClock) {
      return undefined
    }

    const interval = window.setInterval(() => {
      setCurrentTime(CLOCK_FORMATTER.format(new Date()))
    }, CLOCK_UPDATE_INTERVAL_MS)

    return () => {
      window.clearInterval(interval)
    }
  }, [showClock])

  return (
    <header
      className={cn(
        'flex h-16 items-center justify-between border-b bg-bg-base border-border-default px-4 md:px-6 shrink-0 z-40 transition-colors',
        className,
      )}
    >
      <div className="flex items-center gap-4 flex-1">
        {onToggleSidebarMobile && (
          <div className="md:hidden flex items-center justify-center h-9 w-9 rounded-xl bg-primary/10 text-primary shrink-0 overflow-hidden">
            {logo?.imageSrc ? (
              <img src={logo.imageSrc} alt={logo.text} className="h-6 w-6 object-contain" />
            ) : LogoIcon ? (
              <LogoIcon weight="Bold" className="h-5 w-5" />
            ) : (
              <Basketball weight="Bold" className="h-5 w-5" />
            )}
          </div>
        )}
        {breadcrumbs && (
          <div className="hidden md:flex min-w-0 flex-1 items-center">{breadcrumbs}</div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-2">
        {showClock && (
          <div className="hidden sm:inline-flex h-10 items-center gap-2 rounded-xl border border-border-subtle bg-bg-subtle px-3">
            <ClockSquare className="h-4 w-4 text-text-tertiary" />
            <span className="text-sm font-semibold tabular-nums text-text-secondary">
              {currentTime}
            </span>
          </div>
        )}

        {themeButton || (
          <button
            type="button"
            onClick={onToggleTheme}
            className="h-10 w-10 flex items-center justify-center rounded-xl hover:bg-bg-muted text-text-secondary transition-colors"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <MoonStars className="h-5 w-5" />}
          </button>
        )}

        <div className="h-8 w-px bg-border-subtle mx-1 sm:mx-2" />

        {userAction || (
          <button type="button" className="flex items-center gap-3 pl-1 sm:pl-2 group">
            {user && (
              <div className="text-right hidden sm:block">
                <p className="text-sm font-semibold text-text-primary leading-none">{user.name}</p>
                <p className="text-xs text-text-tertiary mt-1">{user.role}</p>
              </div>
            )}
            <Avatar>
              {user?.avatarUrl && (
                <AvatarImage src={user.avatarUrl} alt={user?.name} className="object-cover" />
              )}
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold rounded-xl">
                {user?.avatarFallback || 'U'}
              </AvatarFallback>
            </Avatar>
          </button>
        )}
      </div>
    </header>
  )
}
